import { useState, useEffect, useRef } from 'react';
import { FaLightbulb, FaSearch, FaBookOpen, FaCogs, FaUsers, FaSyncAlt, FaRocket, FaCode, FaMicrochip } from 'react-icons/fa';
import profileImage from '../JASMIN.png';
import './About.css';

function About() {
  const [visibleSections, setVisibleSections] = useState(new Set());
  const sectionRefs = useRef([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.dataset.section]));
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -50px 0px' }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  const strengths = [
    { icon: <FaLightbulb />, title: 'Problem-Solving', desc: 'Analytical thinker who thrives on tackling complex challenges' },
    { icon: <FaSearch />, title: 'Attention to Detail', desc: 'Meticulous approach to producing high-quality work' },
    { icon: <FaBookOpen />, title: 'Willingness to Learn', desc: 'Continuously exploring new technologies and methodologies' },
    { icon: <FaCogs />, title: 'Technical Curiosity', desc: 'Passionate about understanding how systems work under the hood' },
    { icon: <FaUsers />, title: 'Team Collaboration', desc: 'Effective communicator who values shared progress' },
    { icon: <FaSyncAlt />, title: 'Adaptability', desc: 'Flexible and resilient in fast-changing environments' },
  ];

  return (
    <section className="about-page">
      {/* Page Header */}
      <div
        className={`about-header ${visibleSections.has('header') ? 'visible' : ''}`}
        data-section="header"
        ref={(el) => (sectionRefs.current[0] = el)}
      >
        <div className="header-bg-shapes">
          <div className="shape shape-1"></div>
          <div className="shape shape-2"></div>
          <div className="shape shape-3"></div>
        </div>
        <div className="about-header-content">
          <h1 className="page-title">About Me</h1>
          <p className="page-subtitle">Getting to know who I am, what drives me, and where I'm headed</p>
        </div>
      </div>

      {/* Introduction */}
      <div className="about-body">
        <div className="about-container">
          <div
            className={`intro-section ${visibleSections.has('intro') ? 'visible' : ''}`}
            data-section="intro"
            ref={(el) => (sectionRefs.current[1] = el)}
          >
            <div className="intro-card">
              <div className="intro-accent"></div>
              <div className="intro-image-wrapper">
                <img src={profileImage} alt="Jasmin Meghan R. Malolos" className="intro-photo" />
              </div>
              <div className="intro-text-wrapper">
                <p className="intro-text">
                  Hello! I am <span className="name-highlight">Jasmin Meghan R. Malolos</span>, 
                  a third-year Computer Engineering student at <span className="school-highlight">FAITH Colleges</span>. 
                  I have a strong interest in understanding how technology works—from the 
                  hardware that powers systems to the software that enables them to function efficiently.
                </p>
              </div>
            </div>
          </div>

          {/* Journey Cards */}
          <div
            className={`journey-section ${visibleSections.has('journey') ? 'visible' : ''}`}
            data-section="journey"
            ref={(el) => (sectionRefs.current[2] = el)}
          >
            <h2 className="creative-title">
              <span className="title-line"></span>
              My Journey
              <span className="title-line"></span>
            </h2>

            <div className="journey-cards">
              <div className="journey-card">
                <div className="journey-card-icon">
                  <FaRocket />
                </div>
                <h3 className="journey-card-title">How It Started</h3>
                <p className="journey-card-text">
                  My interest in technology started during my Grade 12 years when I had the opportunity 
                  to create a website using HTML. That experience allowed me to see how ideas can be 
                  transformed into something interactive and functional, which sparked my curiosity and 
                  became one of the main reasons I chose to pursue Computer Engineering.
                </p>
              </div>

              <div className="journey-card">
                <div className="journey-card-icon">
                  <FaCode />
                </div>
                <h3 className="journey-card-title">Growing My Skills</h3>
                <p className="journey-card-text">
                  As I continue my academic journey, I am developing my skills in both hardware and 
                  software through hands-on projects and technical coursework. I am particularly interested 
                  in software development and IoT-based systems, where I can apply my knowledge to create 
                  practical and innovative solutions.
                </p>
              </div>

              <div className="journey-card">
                <div className="journey-card-icon">
                  <FaMicrochip />
                </div>
                <h3 className="journey-card-title">Current Projects</h3>
                <p className="journey-card-text">
                  I am currently working on several projects that allow me to apply my knowledge in 
                  real-world scenarios. These include an Ambient Light-Based Smart Lighting System, 
                  an IoT-based project that aims to automate lighting depending on environmental conditions, 
                  and a restaurant web application, where I am applying both frontend and backend development 
                  concepts learned from workshops. These projects are still in progress and continue to help 
                  me improve my technical and problem-solving skills.
                </p>
              </div>
            </div>
          </div>

          {/* Vision Banner */}
          <div
            className={`vision-banner ${visibleSections.has('vision') ? 'visible' : ''}`}
            data-section="vision"
            ref={(el) => (sectionRefs.current[3] = el)}
          >
            <div className="vision-content">
              <h3 className="vision-title">My Vision</h3>
              <p className="vision-text">
                In the future, I aim to become a competent and innovative technology professional who can 
                contribute to building efficient, reliable, and user-centered systems. I value continuous 
                learning and strive to improve my skills to keep up with the evolving field of technology.
              </p>
            </div>
            <div className="vision-decoration">
              <div className="deco-circle deco-1"></div>
              <div className="deco-circle deco-2"></div>
              <div className="deco-circle deco-3"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Strengths Section */}
      <div
        className={`strengths-showcase ${visibleSections.has('strengths') ? 'visible' : ''}`}
        data-section="strengths"
        ref={(el) => (sectionRefs.current[4] = el)}
      >
        <div className="about-container">
          <h2 className="creative-title light">
            <span className="title-line light"></span>
            Personal Strengths
            <span className="title-line light"></span>
          </h2>
          <div className="strengths-grid">
            {strengths.map((s, index) => (
              <div
                key={index}
                className="strength-card"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="strength-icon-wrapper">
                  {s.icon}
                </div>
                <h4 className="strength-title">{s.title}</h4>
                <p className="strength-desc">{s.desc}</p>
                <div className="strength-hover-bar"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

export default About;
